import './editor.scss';

import React, { useState } from 'react';
import { Icon, replace, group, mobile } from '@wordpress/icons';
import { _ } from 'lodash';
import { sha1 } from 'js-sha1';

// Internal dependencies
import { MOBILE_CONTENT } from '@constants';
import {
  InspectorTabs,
  InspectorControlsGeneral,
  InspectorControlsDeveloper,
} from '@components/inspector-tabs';
import Label from '@components/block-label';

const {
  blockEditor: { InspectorControls, useBlockProps, InnerBlocks, useSetting },
  element: { useEffect },
  components: {
    TabPanel,
    PanelBody,
    Button,
    Tooltip,
    Modal,
    Panel,
    ToggleControl,
  },
  i18n: { __ },
  data: { select, withSelect },
} = wp;

const MobileTabConfiguration = ({ attributes, setAttributes }) => {
  const { activeTab, enableMobileTab, nonIdenticalBlockStructure } =
    attributes || {};
  return (
    <Panel>
      <PanelBody>
        {!nonIdenticalBlockStructure && activeTab === 'desktop' && (
          <ToggleControl
            label={__('Enable mobile tab', 'b3rg')}
            checked={enableMobileTab}
            onChange={(enableMobileTab) => {
              setAttributes({ enableMobileTab });
              if (!enableMobileTab) {
                setAttributes({ activeTab: 'desktop' });
              }
            }}
          />
        )}
      </PanelBody>
    </Panel>
  );
};

const Edit = (props) => {
  const { name, attributes, setAttributes, environment, siteUrl } = props;
  const { activeTab, enableMobileTab, nonIdenticalBlockStructure, label } =
    attributes;
  const blockProps = useBlockProps();
  const [initialTabName] = useState('desktop');
  const [desktopTabBlockObjects, setDesktopTabBlockObjects] = useState([]);
  const [mobileTabBlockObjects, setMobileTabBlockObjects] = useState([]);
  const [isIdentical, setIsIdentical] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setAttributes({ activeTab: initialTabName });
  }, [initialTabName, setAttributes]);

  const innerBlockProps = {
    template: [
      ['b3rg/desktop-content-wrapper'],
      ['b3rg/mobile-content-wrapper'],
    ],
    templateLock: 'all',
  };

  // To toggle the active tab
  const onTabSelect = (activeTab) => {
    setAttributes({ activeTab });
  };

  const [desktopBlockId, mobileBlockId] = select(
    'core/block-editor',
  ).getBlockOrder(props.clientId);

  // Clone all the inner content from the source block to the target block
  const cloneFromSourceToTarget = (sourceBlockId, targetBlockId) => {
    const sourceBlock = wp.data
      .select('core/block-editor')
      .getBlock(sourceBlockId);
    // Function to recursively update the client ids of the inner blocks
    const updateBlocks = (blocks) => {
      return blocks.map((block) => {
        const newBlock = wp.blocks.createBlock(
          block.name,
          block.attributes,
          updateBlocks(block.innerBlocks),
        );
        return newBlock;
      });
    };
    // Get the inner blocks of the source block and update them
    const updatedInnerBlocks = updateBlocks(sourceBlock.innerBlocks);
    const targetBlock = wp.data
      .select('core/block-editor')
      .getBlock(targetBlockId);
    wp.data
      .dispatch('core/block-editor')
      .insertBlocks(
        updatedInnerBlocks,
        targetBlock.innerBlocks.length,
        targetBlockId,
      );
  };

  let desktopTabBlockTreeData = [];
  let mobileTabBlockTreeData = [];

  // Ignoring the defined blocks from the block trees before the comparison
  const generateBlockTreeArray = (
    blocks,
    ignoreFromBlockTree,
    desktop = false,
  ) => {
    const blockTreeArray = [];
    blocks.forEach((block) => {
      if (typeof block.clientId !== 'undefined') {
        const currentBlockParentIds = wp.data
          .select('core/block-editor')
          .getBlockParents(block.clientId);
        const currentBlockParents = wp.data
          .select('core/block-editor')
          .getBlocksByClientId(currentBlockParentIds);

        const currentBlockParentNames =
          currentBlockParents.length && currentBlockParents[0] !== null
            ? _.map(currentBlockParents, 'name')
            : [];
        const intersection = _.intersection(
          ignoreFromBlockTree,
          currentBlockParentNames,
        );
        const hashedClientId = sha1.hex(block.clientId);

        if (desktop) {
          block.clientId = hashedClientId;
        }

        if (
          !ignoreFromBlockTree.includes(block.name) &&
          Array.isArray(intersection) &&
          intersection.length === 0
        ) {
          blockTreeArray.push(block);
        }
      }
    });
    return blockTreeArray;
  };

  const checkIfBlockTreesAreIdentical = (
    sourceBlock,
    targetBlock,
    ignoreFromBlockTree,
  ) => {
    desktopTabBlockTreeData = [];
    mobileTabBlockTreeData = [];

    const desktopTabBlockIds = wp.data
      .select('core/block-editor')
      .getClientIdsOfDescendants([sourceBlock]);
    const desktopTabBlockData = wp.data
      .select('core/block-editor')
      .getBlocksByClientId(desktopTabBlockIds);

    const mobileTabBlockIds = wp.data
      .select('core/block-editor')
      .getClientIdsOfDescendants([targetBlock]);
    const mobileTabBlockData = wp.data
      .select('core/block-editor')
      .getBlocksByClientId(mobileTabBlockIds);

    desktopTabBlockTreeData = generateBlockTreeArray(
      desktopTabBlockData,
      ignoreFromBlockTree,
      true,
    );
    setDesktopTabBlockObjects(desktopTabBlockTreeData);

    mobileTabBlockTreeData = generateBlockTreeArray(
      mobileTabBlockData,
      ignoreFromBlockTree,
    );
    setMobileTabBlockObjects(mobileTabBlockTreeData);

    let identical = false;
    if (desktopTabBlockTreeData.length && mobileTabBlockTreeData.length) {
      identical = _.isEqual(
        _.map(desktopTabBlockTreeData, 'name'),
        _.map(mobileTabBlockTreeData, 'name'),
      );
      setIsIdentical(identical);
    }
    return identical;
  };

  const replaceAttributesFromSourceToTarget = (ignoreFromReplacing = []) => {
    for (let index = 0; index < desktopTabBlockObjects.length; index++) {
      if (ignoreFromReplacing.includes(desktopTabBlockObjects[index].name)) {
        continue;
      }
      if (
        typeof mobileTabBlockObjects[index].clientId !== 'undefined' &&
        typeof desktopTabBlockObjects[index].clientId !== 'undefined' &&
        typeof desktopTabBlockObjects[index].innerBlocks !== 'undefined'
      ) {
        wp.data
          .dispatch('core/block-editor')
          .replaceInnerBlocks(
            mobileTabBlockObjects[index].clientId,
            desktopTabBlockObjects[index].innerBlocks,
          );
      }
    }
    setIsOpen(false);
  };

  // eslint-disable-next-line no-unused-vars
  const configurePopupModal = (
    sourceBlock,
    targetBlock,
    ignoreFromBlockTree = [],
  ) => {
    const identical = checkIfBlockTreesAreIdentical(
      sourceBlock,
      targetBlock,
      ignoreFromBlockTree,
    );
    setIsIdentical(identical);
    setIsOpen(true);
  };

  // Generates a custom preview url for the block and calls to an ajax function to load the preview template.
  const blockPreviewModeUrl =
    siteUrl +
    '/wp-admin/admin-ajax.php?action=preview_mode&post=' +
    wp.data.select('core/editor').getCurrentPostId();

  const toolbarSettings = useSetting(`custom.blocks.${name}.toolbarSettings`);

  return (
    <div {...blockProps} key={blockProps.id}>
      {isOpen && (
        <Modal
          className="mobile-content-modal"
          title={__('Replace content from Desktop', 'b3rg')}
          onRequestClose={() => setIsOpen(false)}
        >
          <p>
            {isIdentical
              ? __(
                  'This will replace all the the content and block settings of the mobile tab from the desktop tab excluding the layout blocks. Are you sure you want to proceed?',
                  'b3rg',
                )
              : __(
                  'Cannot copy and replace the content since the block trees are not identical in both desktop and mobile tabs',
                  'b3rg',
                )}
          </p>
          <div className="mobile-content-modal__button-wrapper">
            {isIdentical && (
              <Button
                variant="primary"
                onClick={() =>
                  replaceAttributesFromSourceToTarget(
                    MOBILE_CONTENT.IGNORE_FROM_REPLACING_INNERBLOCKS,
                  )
                }
              >
                {__('Copy and replace the content', 'b3rg')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              {__('Close')}
            </Button>
          </div>
        </Modal>
      )}
      <InspectorControls>
        {(environment === 'development' ||
          (environment === 'production' && !nonIdenticalBlockStructure)) && [
          <>
            {/* These settings will be displayed only on the Development environment */}
            {environment === 'development' ? (
              <InspectorTabs>
                <InspectorControlsGeneral>
                  <Label {...{ label, setAttributes }} />
                  <MobileTabConfiguration {...{ attributes, setAttributes }} />
                </InspectorControlsGeneral>
                <InspectorControlsDeveloper>
                  <ToggleControl
                    label={__(
                      'Mobile structure different from desktop',
                      'b3rg',
                    )}
                    checked={nonIdenticalBlockStructure}
                    help={__(
                      'Please enable this option when the block structures of the two blocks are not identical. Ex: A Slider for desktop and an Accordion for mobile',
                      'b3rg',
                    )}
                    onChange={(nonIdenticalBlockStructure) => {
                      setAttributes({ nonIdenticalBlockStructure });
                      if (nonIdenticalBlockStructure) {
                        setAttributes({ enableMobileTab: true });
                      }
                    }}
                  />
                </InspectorControlsDeveloper>
              </InspectorTabs>
            ) : (
              <>
                <Label {...{ label, setAttributes }} />
                <MobileTabConfiguration {...{ attributes, setAttributes }} />
              </>
            )}
          </>,
        ]}
      </InspectorControls>
      <div className="mobile-content-tabs">
        <div className="mobile-content-tabs__button-wrapper">
          <div>
            {activeTab === 'mobile' && !nonIdenticalBlockStructure && (
              <>
                <Tooltip text={__('Duplicate content from Desktop', 'b3rg')}>
                  <Button
                    variant="secondary"
                    className="duplicate-content"
                    onClick={() =>
                      cloneFromSourceToTarget(desktopBlockId, mobileBlockId)
                    }
                  >
                    <Icon icon={group} />
                  </Button>
                </Tooltip>
                {toolbarSettings?.replaceContent && (
                  <Tooltip text={__('Replace content from desktop', 'b3rg')}>
                    <Button
                      variant="secondary"
                      className="replace-content"
                      onClick={() =>
                        configurePopupModal(
                          desktopBlockId,
                          mobileBlockId,
                          MOBILE_CONTENT.IGNORE_FROM_BLOCK_TREE,
                        )
                      }
                    >
                      <Icon icon={replace} />
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip text={__('Mobile preview', 'b3rg')}>
              <Button
                variant="secondary"
                className="preview-content"
                href={blockPreviewModeUrl}
                target="_blank"
              >
                <Icon icon={mobile} />
              </Button>
            </Tooltip>
          </div>
        </div>
        <TabPanel
          activeClass="bs-active-tab"
          orientation="horizontal"
          initialTabName={
            initialTabName !== activeTab ? initialTabName : activeTab
          }
          onSelect={onTabSelect}
          tabs={[
            {
              name: 'desktop',
              title: __('Desktop', 'b3rg'),
              className: 'tab-desktop',
            },
            {
              name: 'mobile',
              title: (
                <>
                  {__('Mobile', 'b3rg')}
                  <Tooltip
                    position="top right"
                    text={__(
                      'The block tree structures of the two tabs have been identified as non-identical',
                      'b3rg',
                    )}
                  >
                    <span className="tooltip-icon">?</span>
                  </Tooltip>
                </>
              ),
              className: `tab-mobile ${
                nonIdenticalBlockStructure && `tab-non-identical`
              } ${!enableMobileTab && `tab-disabled`}`, //NOSONAR,
            },
          ]}
        >
          {(tab) => (
            <PanelBody>
              <div className={`${tab.className} bs-active`}>
                <InnerBlocks {...innerBlockProps} />
              </div>
            </PanelBody>
          )}
        </TabPanel>
      </div>
    </div>
  );
};

export default withSelect((select) => {
  const theme = select('core').getCurrentTheme();
  const siteData = select('core').getSite();
  const { wp_environment_type: environment = 'production' } = theme || {};
  const { url: siteUrl } = siteData || {};
  return { environment, siteUrl };
})(Edit);
